import { Component } from "react";
import DocTypeConfig from "../models/config/DocTypeConfig";
import Icon from "./Icon";
import { VisibilityToggle } from "./buttons/VisibilityToggle";
import { ToggleButton } from "./buttons/ToggleButton";

import "../styles/dlfTabList.css";
import { flushSync } from "react-dom";

type Props = {
  tabsChanged: (selectedTabs: string[]) => void;
  docType: DocTypeConfig;
};

type State = {
  displayTabs: string[];
  selectedTabs: string[];
};

/**
 * Class provides functionality to do the following
 * - gets the tabs to display, includes loading from session stroage
 * - allow updating tabs, includes setting the session storage
 * - toggle tab, but prevent hiding all fields at once
 * - render tab display list
 */
export default class DLFTabList extends Component<Props, State> {
  static SS_SelectedDisplayTabs: string =
    "ACC_DLFTABLIST_SELECTED_DISPLAY_TABS";

  constructor(props: Readonly<Props>) {
    super(props);

    this.state = this.buildTabsStateFromDocType(this.props.docType);
    this.props.tabsChanged(this.state.selectedTabs);
  }

  setStateSync: typeof this.setState = (...args) => {
    flushSync(() => {
      this.setState(...args);
    });
  };

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (this.props.docType !== newProps.docType) {
      this.setStateSync(this.buildTabsStateFromDocType(newProps.docType), () =>
        this.props.tabsChanged(this.state.selectedTabs),
      );
    }
  }

  buildTabsStateFromDocType(docType: DocTypeConfig): State {
    const displayTabs: string[] = this.constructDisplayTabsArray(docType);
    const selectedTabs: string[] =
      this.getSelectedTabsFromNewDisplayTabs(displayTabs);
    const stateObject: State = {
      displayTabs,
      selectedTabs,
    };
    return stateObject;
  }

  /**
   * gets a distinct list of DisplayTabs, including an uncategorized display list
   */
  constructDisplayTabsArray(docType: DocTypeConfig): string[] {
    const displayTabs: string[] = [];
    // create set of display tabs
    const displayTabSet = new Set(
      docType.fields.filter((f) => !f.actualHidden).map((f) => f.displayTab),
    );
    displayTabSet.forEach((x) => displayTabs.push(x));
    // shift '' to end if it exists
    if (displayTabSet.has("")) {
      displayTabs.push(...displayTabs.splice(displayTabs.indexOf(""), 1));
    }
    return displayTabs;
  }

  getSelectedTabsFromNewDisplayTabs(displayTabs: string[]): string[] {
    // Get saved data from sessionStorage
    const data_serialized = sessionStorage.getItem(
      DLFTabList.SS_SelectedDisplayTabs,
    );
    let data: string[] = [];
    // process data, make sure its not broken
    try {
      data = JSON.parse(data_serialized ?? "");
      // unsure what type `data` is, just yolo it
      data = (data as string[])
        // filter out anything that isn't in our list
        .filter((x) => displayTabs.indexOf(x) != -1);
    } catch (e) {
      data = [];
    }

    // don't let nothing be shown
    if (data.length == 0) data = displayTabs;

    return data;
  }

  updateSelectedTabsSessionStorage(state: State) {
    sessionStorage.setItem(
      DLFTabList.SS_SelectedDisplayTabs,
      JSON.stringify(state.selectedTabs),
    );
  }

  /**
   * should prevent no fields from being visibile
   * @param displayTab
   * @param isVisible
   */
  toggleTab(displayTab: string, isVisible: boolean) {
    if (isVisible) {
      if (this.state.selectedTabs.length === 1) return; // don't allow hiding of all fields
      this.setStateSync(
        (s) => ({
          selectedTabs: [...s.selectedTabs.filter((x) => x !== displayTab)],
        }),
        () => {
          this.updateSelectedTabsSessionStorage(this.state);
          this.props.tabsChanged(this.state.selectedTabs);
        },
      );
    } else {
      this.setStateSync(
        (s) => ({
          selectedTabs: [...s.selectedTabs, displayTab],
        }),
        () => {
          this.updateSelectedTabsSessionStorage(this.state);
          this.props.tabsChanged(this.state.selectedTabs);
        },
      );
    }
  }

  render() {
    if (this.state.displayTabs.length === 1) {
      return <></>;
    }
    return (
      /** TODO: this should be it's own component probably */
      <div className={"displayTabList"}>
        {/* {this.state.displayTabs.length === 1 && (
                    <ToggleButton active={true}
                        onClick={() => null}
                        className={'dlfTabButton'}>
                        {this.state.displayTabs[0] !== '' ? this.state.displayTabs[0] : 'Uncategorized'}
                    </ToggleButton>
                )} */}
        {this.state.displayTabs.length > 1 &&
          this.state.displayTabs.map((tab) => {
            const isUncategorized = tab === "";
            const isEnabled = this.state.selectedTabs.indexOf(tab) > -1;
            return (
              <VisibilityToggle
                key={tab}
                active={isEnabled}
                onClick={() => this.toggleTab(tab, isEnabled)}
                className={
                  "dlfTabButton " + (isUncategorized ? "uncategorized" : "")
                }
                altText={`${isEnabled ? "Hide" : "Show"} DLF Group ${tab}`}
              >
                {!isUncategorized ? tab : "Uncategorized"}
              </VisibilityToggle>
            );
          })}
      </div>
    );
  }
}
